<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { downloadFile } from '../../common/util/util.js';
    import { fsType } from "./enums.js";
    import { tick } from 'svelte';
    import { confirmable, tooltip } from '../../common/confirmable.js';

    // Import icons so Vite can process them
    import iconFolder from '../../../img/icon-folder.svg';
    import iconFolderOpened from '../../../img/icon-folder-opened.svg';
    import iconUpload from '../../../img/icon-upload.svg';
    import iconAddFolder from '../../../img/icon-add-folder.svg';
    import iconEdit from '../../../img/icon-edit.svg';
    import iconTrash from '../../../img/trash.svg';
    import iconLock from '../../../img/icon-lock.svg';
    import iconLockOpened from '../../../img/icon-lock-opened.svg';
    import iconStar from '../../../img/icon-star.svg';
    import iconStarEmpty from '../../../img/icon-star-empty.svg';

    const dispatch = createEventDispatcher();
    const appPrefix = window.appPrefix || '/projects';
    const supportedIcons = ['ppt', 'doc', 'txt', 'pdf', 'xls', 'jpg', 'zip', 'png', 'gif']
    const extensionMap = {'docx': 'doc', 'xlsx': 'xls'}
    const readonly = !document.getElementById('project-form')?.dataset.autosaveUrl;
    const confirmMainParams = {prompt: "Do you want to use this file's thumbnail?"};
    const confirmDeleteParams = {prompt: "Are you sure you want to delete this?"};

	export let name = "";
	export let children = [];
    export let editing = false;
    export let parent = {};
    export let type = fsType.FOLDER;
    export let id = null;
    export let confidential = false;
    export let isMainDoc = false;
    export let number = 0; // Index for Svelte bookkeeping

	let newName;
	let expanded = true;

    $: extension = name.slice(name.lastIndexOf('.') + 1);
    $: typeStr = type == fsType.FOLDER ? 'folder': 'file';
    $: bg =  type == fsType.FILE ? `background-image: url(/static/icon-${extensionMap[extension] || supportedIcons.find(i => i == extension) || 'file'}.svg)` : '';
    $: isFolder = type == fsType.FOLDER;

    // Sort children: folders first, then starred files, then alphabetically
    $: sortedChildren = [...children].sort((a, b) => {
        // Folders before files
        if (a.type !== b.type) {
            return a.type === fsType.FOLDER ? -1 : 1;
        }
        // For files: starred (main) documents first
        if (a.type === fsType.FILE) {
            if (a.isMainDoc !== b.isMainDoc) {
                return a.isMainDoc ? -1 : 1;
            }
        }
        // Then alphabetically by name
        return a.name.localeCompare(b.name);
    });

    onMount(() => {
        if (newName) enableEditing();
    });
    
    function confirmThumbnail(event) {
        fetch(`${appPrefix}/update_thumbnail/?doc_id=${id}`)
        .then( res => {
            if (!res.ok) { throw res }
            return res.json();
        }).then(json => {
            if(json.image) {
                document.getElementById('project-img').setAttribute('src', json.image)
            }
            else window.alert('Image extraction failed');
        }).catch( err => {
            err.text().then( errorMessage => console.log(errorMessage) );
        });
        event.detail.hide();
    }
    
    function cancelChoice(event) { event.detail.hide();  }
	function toggleExpand() { expanded = !expanded; }

    function sendMessage(what, extra) {
        dispatch('message', {
            what: what,
            name: name,
            parent: parent,
            ...extra
        });

    }
    function addFolder(){
        expanded = true;
        sendMessage('addFolder', {folderName: 'New folder'});
    }

	function addFile() {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.style.display="none";
        input.addEventListener('change', e => {
            dispatch('message', {
                what: 'addFile',
                name: name,
                file: e.target.files[0],
                parent: parent,
            });
        });
        input.click();
    }

    function editName(){
        sendMessage('editName', {newName: newName.value});
    }
    function getFile(){
        downloadFile(`${appPrefix}/download_document/?doc_id=${id}`);
    }
    function setMain() {
        sendMessage('setMain', {main: !isMainDoc});
    }
    function onShowConfirmThumbnail(event) {
        if (isMainDoc) setTimeout(()=> event.detail.hide(), 0);
    }
    function setConfidential() {
        if (readonly) return;
        sendMessage('setConfidential', {confidential: !confidential});
    }
    function deleteNode() {
        sendMessage('deleteNode', {});
    }
    function handleKeydown(event) {
        if (event.keyCode == 13) { // enter key
            editName();
        }
	}
    async function enableEditing() {
        editing = true;
        await tick();
        newName.focus();
        newName.value = name;
    }


</script>

<style>
    .wrapper {
        display: flex;
        width: 100%;
        height: 1.1rem;
        font-size: 1.1rem;
        flex-flow: row nowrap;
        align-items: center;
    }
    .hidden {
        display: none;
    }
    .main {
        display: inline;
        height: 1rem;
        padding: 0 1rem 0 1.5rem;
        background: 0 0 no-repeat;
        background-size: 1rem 1rem;
        cursor: pointer;
    }
    .main:hover:not(input) {
        color: #16706a;
    }

	.folder {
		font-weight: bold;
		cursor: pointer;
	}

	ul {
		padding: 0.2rem 0 0 0.5rem;
		margin: 0 0 0 0.5rem;
		list-style: none;
		border-left: 1px solid #eee;
	}

	li {
		padding: 0.2rem 0;
	}

    .interaction {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        visibility: hidden;
        cursor: pointer;
        margin: 0 5px;
        background: 0 0 no-repeat;
        background-size: 1rem 1rem;
    }
    .add-folder {
        margin-top: 3px;
    }
    .confidential, .main-doc {
        visibility: visible;
    }
    .wrapper:hover .interaction {
        visibility: visible;
    }

</style>


{#if editing }
    <input bind:this={newName} style={bg} class="main {typeStr}" type="text" on:blur={editName} on:keydown={handleKeydown}>
{/if}
<div class="wrapper" class:hidden="{editing}" data-index={number}>
{#if isFolder}
    <span class="main folder" style="background-image: url({expanded ? iconFolderOpened : iconFolder})" on:click={toggleExpand} on:keydown={e => e.key === 'Enter' && toggleExpand()} tabindex="0" role="button"> {name} </span>
    {#if !readonly}
    <span class="interaction add-folder" style="background-image: url({iconAddFolder})" on:click={addFolder} on:keydown={e => e.key === 'Enter' && addFolder()} tabindex="0" role="button" use:tooltip title="Add folder"></span>
    <span class="interaction" style="background-image: url({iconUpload})" on:click={addFile} on:keydown={e => e.key === 'Enter' && addFile()} tabindex="0" role="button" use:tooltip title="Upload file"></span>
    {#if parent != null}
        <span class="interaction" style="background-image: url({iconEdit})" on:click={enableEditing} on:keydown={e => e.key === 'Enter' && enableEditing()} tabindex="0" role="button" use:tooltip title="Change name"> </span>
        <span class="interaction" style="background-image: url({iconTrash})" use:confirmable={confirmDeleteParams} on:confirmation={deleteNode} on:cancel={cancelChoice} tabindex="0" role="button">  </span>
    {/if}
    {/if}
{:else}
    <span class="main" style={bg} on:click={getFile} on:keydown={e => e.key === 'Enter' && getFile()} tabindex="0" role="button"> {name} </span>
    <span class="interaction {confidential ? 'confidential': ''}" style="background-image: url({confidential ? iconLock : iconLockOpened})" on:click={setConfidential} on:keydown={e => e.key === 'Enter' && setConfidential()} tabindex="0" role="button" use:tooltip title="Set/unset confidential"> </span>
    {#if !readonly}
    <span use:confirmable={confirmMainParams} class="interaction {isMainDoc ? 'main-doc': ''}" style="background-image: url({isMainDoc ? iconStar : iconStarEmpty})"
        on:click={setMain} on:keydown={e => e.key === 'Enter' && setMain()} tabindex="0" role="button" on:confirmation={confirmThumbnail} on:cancel={cancelChoice} on:show={onShowConfirmThumbnail} use:tooltip title="Main document of the project"> </span>
    <span class="interaction" style="background-image: url({iconEdit})" on:click={enableEditing} on:keydown={e => e.key === 'Enter' && enableEditing()} tabindex="0" role="button" use:tooltip title="Change name"> </span>
    <span class="interaction" style="background-image: url({iconTrash})" use:confirmable={confirmDeleteParams} on:confirmation={deleteNode} on:cancel={cancelChoice} tabindex="0" role="button"> </span>
    {/if}
{/if}
</div>
{#if expanded}
<ul>
	{#each sortedChildren as child (child.number)}
    <li>
        <svelte:self {...child} on:message/>
    </li>
	{/each}
</ul>
{/if}

